import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/design-system'
import { APP_NAME } from '@/utils/constants'
import { LogoMark } from '@/features/album/components/EgowallLogo'

/**
 * Vision mock — "what if this gallery became a Space?"
 * Not the egospace runtime. Shows the realistic-gallery destination
 * (walkable place) plus proposed design layers Virtual had:
 * Furniture · Photos · Notes · Shop.
 */

type LayerId = 'furniture' | 'photos' | 'notes' | 'shop'

const LAYERS: {
  id: LayerId
  label: string
  eyebrow: string
  blurb: string
  options: string[]
}[] = [
  {
    id: 'furniture',
    label: 'Furniture',
    eyebrow: 'From Virtual Spaces',
    blurb: 'Place desks, stools, benches — the room becomes inhabited, not just hung.',
    options: [
      'Spawn from inventory (Virtual parity)',
      'Move / rotate / scale gizmos',
      'Collision so walk doesn’t clip',
      'Template kits per room style',
    ],
  },
  {
    id: 'photos',
    label: 'Photos',
    eyebrow: `${APP_NAME} core`,
    blurb: 'What Studio already does — prints on walls — becomes the heart of the Space.',
    options: [
      'Freeform hang (Studio today)',
      'Classic diorama slots',
      'Frames + mats + spotlights',
      'Guest Story tour across rooms',
    ],
  },
  {
    id: 'notes',
    label: 'Notes',
    eyebrow: 'Social layer',
    blurb: 'Sticky notes, mail, pin comments — visitors leave something behind.',
    options: [
      'Spatial sticky notes',
      'Pin comments on prints (Studio pins)',
      'Guestbook at the door',
      'Visit activity ticker',
    ],
  },
  {
    id: 'shop',
    label: 'Shop',
    eyebrow: 'Economy / assets',
    blurb: 'Space templates, furniture SKUs, scene packs — buy the place, then decorate.',
    options: [
      'Living-space templates (Mountain Lot…)',
      'Furniture & prop catalog',
      `Scene packs for ${APP_NAME} plans`,
      'Ego Points → modern plan unlocks',
    ],
  },
]

const Root = styled(Box)({
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
  background: '#050508',
  color: 'var(--ds-night-ink, #f4f3f6)',
  fontFamily: 'var(--ds-font)',
})

const Stage = styled(Box)({
  position: 'absolute',
  inset: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center 40%',
  transform: 'scale(1.04)',
  animation: 'vsKen 28s ease-in-out infinite alternate',
  '@keyframes vsKen': {
    from: { transform: 'scale(1.04) translate3d(0,0,0)' },
    to: { transform: 'scale(1.1) translate3d(-1%, -0.6%, 0)' },
  },
})

const Veil = styled(Box)({
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(105deg, rgba(5,5,8,0.82) 0%, rgba(5,5,8,0.35) 42%, rgba(5,5,8,0.55) 100%), linear-gradient(180deg, rgba(5,5,8,0.25) 0%, transparent 40%, rgba(5,5,8,0.88) 100%)',
  pointerEvents: 'none',
})

const Top = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 20,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 18px',
})

const Brand = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  fontWeight: 750,
  fontSize: 15,
  letterSpacing: '-0.02em',
})

const Proto = styled(Box)({
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 28,
  padding: '0 12px',
  borderRadius: 999,
  background: 'rgba(19,18,25,0.65)',
  border: '1px solid rgba(244,243,246,0.12)',
  backdropFilter: 'blur(10px)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.12em',
  color: 'rgba(244,243,246,0.55)',
  textTransform: 'uppercase',
  '& i': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#be9dfd',
    boxShadow: '0 0 10px #be9dfd',
    fontStyle: 'normal',
  },
})

const HeroCopy = styled(Box)({
  position: 'absolute',
  left: 28,
  bottom: 110,
  zIndex: 10,
  maxWidth: 480,
  '@media (max-width: 900px)': {
    left: 18,
    right: 18,
    bottom: 'auto',
    top: 80,
    maxWidth: 'none',
  },
})

const Eyebrow = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  background: 'var(--ds-gradient-spectrum)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: 12,
})

const Title = styled(Typography)({
  fontSize: 'clamp(26px, 4vw, 40px)',
  fontWeight: 750,
  letterSpacing: '-0.04em',
  lineHeight: 1.08,
  textShadow: '0 8px 40px rgba(0,0,0,0.55)',
})

const Sub = styled(Typography)({
  marginTop: 12,
  fontSize: 15,
  lineHeight: 1.55,
  color: 'rgba(244,243,246,0.65)',
  maxWidth: 420,
})

const Hud = styled(Box)({
  position: 'absolute',
  left: '50%',
  bottom: 28,
  transform: 'translateX(-50%)',
  zIndex: 15,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 10px 8px 16px',
  borderRadius: 999,
  background: 'rgba(19,18,25,0.88)',
  border: '1px solid rgba(244,243,246,0.12)',
  backdropFilter: 'blur(14px)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: 'rgba(244,243,246,0.55)',
  '@media (max-width: 640px)': { display: 'none' },
})

const Kbd = styled('span')({
  border: '1px solid rgba(244,243,246,0.14)',
  borderRadius: 5,
  padding: '2px 7px',
  background: 'rgba(244,243,246,0.06)',
  color: 'rgba(244,243,246,0.85)',
})

const Panel = styled(Box)({
  position: 'absolute',
  top: 64,
  right: 18,
  bottom: 28,
  width: 380,
  maxWidth: 'calc(100vw - 36px)',
  zIndex: 20,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 18,
  background: 'rgba(14,13,18,0.88)',
  border: '1px solid rgba(244,243,246,0.12)',
  backdropFilter: 'blur(18px) saturate(1.2)',
  boxShadow: '0 28px 80px rgba(0,0,0,0.45)',
  overflow: 'hidden',
  '@media (max-width: 900px)': {
    top: 'auto',
    left: 12,
    right: 12,
    bottom: 12,
    width: 'auto',
    maxWidth: 'none',
    maxHeight: '48vh',
  },
})

const PanelHead = styled(Box)({
  padding: '18px 18px 14px',
  borderBottom: '1px solid rgba(244,243,246,0.08)',
})

const LayerList = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

const LayerCard = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(
  ({ $on }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    display: 'block',
    width: '100%',
    borderRadius: 14,
    padding: '14px 14px 12px',
    cursor: 'pointer',
    background: $on ? 'rgba(244,243,246,0.1)' : 'rgba(244,243,246,0.04)',
    border: $on ? '1px solid rgba(190,157,253,0.45)' : '1px solid rgba(244,243,246,0.08)',
    transition: 'background 160ms ease, border-color 160ms ease',
    '&:hover': { background: 'rgba(244,243,246,0.08)' },
  }),
)

const OptList = styled('ul')({
  margin: '10px 0 0',
  padding: '0 0 0 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  '& li': {
    fontSize: 12.5,
    lineHeight: 1.4,
    color: 'rgba(244,243,246,0.62)',
  },
})

const PanelFoot = styled(Box)({
  padding: 14,
  borderTop: '1px solid rgba(244,243,246,0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

export function VirtualSpacesVisionScreen() {
  const { galleryId = '' } = useParams()
  const navigate = useNavigate()
  const [active, setActive] = useState<LayerId>('furniture')
  const layer = LAYERS.find((l) => l.id === active) || LAYERS[0]

  const backToStudio = () => {
    if (galleryId) navigate(`/galleries/${galleryId}/studio`)
    else navigate('/galleries')
  }
  const openFurniturePrototype = () => {
    if (galleryId) navigate(`/galleries/${galleryId}/spaces-studio`)
  }
  const openMountainLot = () => {
    if (galleryId) navigate(`/galleries/${galleryId}/mountain-lot`)
  }

  return (
    <Root>
      <Stage
        style={{ backgroundImage: 'url(/marketing/landing-v2/a03-mountains.jpg)' }}
        aria-hidden
      />
      <Veil aria-hidden />

      <Top>
        <Brand>
          <LogoMark aria-hidden />
          {APP_NAME}
          <Typography
            component='span'
            sx={{
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 9,
              letterSpacing: '0.14em',
              color: 'rgba(244,243,246,0.4)',
              ml: 0.5,
            }}
          >
            SPACES VISION
          </Typography>
        </Brand>
        <Proto>
          <i />
          Prototype · not egospace
        </Proto>
      </Top>

      <HeroCopy>
        <Eyebrow>Gallery → Space</Eyebrow>
        <Title>Every album, a room worth walking into — then living in.</Title>
        <Sub>
          This is the bridge from Studio (prints on walls) toward Virtual Spaces: furniture, notes,
          and shop assets on the same modern {APP_NAME} stack — not the old DoneJS / Babylon&nbsp;2.5
          runtime.
        </Sub>
        <Box mt={2.5} display='flex' gap={1.25} flexWrap='wrap'>
          <Button variant='spectrum' size='sm' onClick={backToStudio}>
            ← Back to Studio
          </Button>
        </Box>
      </HeroCopy>

      <Hud>
        <span>
          <Kbd>VISION</Kbd> · Furniture · Photos · Notes · Shop
        </span>
      </Hud>

      <Panel>
        <PanelHead>
          <Typography
            sx={{
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 9,
              letterSpacing: '0.14em',
              color: 'rgba(244,243,246,0.4)',
              textTransform: 'uppercase',
              mb: 0.75,
            }}
          >
            Design layers
          </Typography>
          <Typography sx={{ fontSize: 17, fontWeight: 720, letterSpacing: '-0.025em' }}>
            What a Space adds on top of a gallery
          </Typography>
        </PanelHead>

        <LayerList>
          {LAYERS.map((l) => {
            const on = l.id === active
            return (
              <LayerCard key={l.id} type='button' $on={on} onClick={() => setActive(l.id)}>
                <Box display='flex' justifyContent='space-between' alignItems='baseline' gap={1}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.02em' }}>
                    {l.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'var(--ds-font-mono)',
                      fontSize: 8,
                      letterSpacing: '0.12em',
                      color: 'rgba(244,243,246,0.35)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {l.eyebrow}
                  </Typography>
                </Box>
                <Typography sx={{ mt: 0.75, fontSize: 12.5, color: 'rgba(244,243,246,0.55)', lineHeight: 1.45 }}>
                  {l.blurb}
                </Typography>
                {on && (
                  <OptList>
                    {l.options.map((o) => (
                      <li key={o}>{o}</li>
                    ))}
                  </OptList>
                )}
              </LayerCard>
            )
          })}
        </LayerList>

        <PanelFoot>
          <Typography sx={{ fontSize: 11.5, color: 'rgba(244,243,246,0.4)', lineHeight: 1.45 }}>
            Active: <b style={{ color: 'rgba(244,243,246,0.85)' }}>{layer.label}</b> — proposal only.
            Shipping path rebuilds these on {APP_NAME} Studio, not by embedding egospace.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {active === 'furniture' && (
              <Button variant='spectrum' size='sm' onClick={openFurniturePrototype}>
                Try furniture placement →
              </Button>
            )}
            {active === 'shop' && (
              <Button variant='spectrum' size='sm' onClick={openMountainLot}>
                Walk the Mountain Lot →
              </Button>
            )}
            <Button variant='night' size='sm' onClick={backToStudio}>
              Return to gallery studio
            </Button>
          </Box>
        </PanelFoot>
      </Panel>
    </Root>
  )
}
